import clsx from 'clsx';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { HighScoreEntity, SingSetup, Song } from '~/interfaces';
import { Button } from '~/modules/elements/akui/button';
import { Icon } from '~/modules/elements/akui/icon';
import useKeyboardNav, { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import ScoreboardPanel from '~/modules/scoreboard/scoreboard-panel';
import ScoreboardRow, { ScoreboardPlaceholderRow } from '~/modules/scoreboard/scoreboard-row';
import { useEditScore } from '~/modules/songs/stats/hooks';
import { useProfiles } from '~/modules/user-profiles/hooks';
import LeaderboardPrompt from '~/routes/game/singing/post-game/views/leaderboard/leaderboard-prompt';
import LeaderboardSharePanel from '~/routes/game/singing/post-game/views/leaderboard/leaderboard-share-panel';
import SongLeaderboardPanel from '~/routes/game/singing/post-game/views/leaderboard/song-leaderboard-panel';
import useLeaderboardPostGame from '~/routes/game/singing/post-game/views/leaderboard/use-leaderboard-post-game';
import { useNextUp } from '~/routes/game/singing/post-game/views/next-up';
import { NextUpLabel } from '~/routes/game/singing/post-game/views/next-up-label';
import SimilarSongs from '~/routes/game/singing/post-game/views/similar-songs';

import { isRecordedUnderAProfile } from './high-score-name';
import HighScoreRename from './high-score-rename';

/**
 * The two boards, side by side from tablet up and stacked below it. Their own height is
 * `ScoreboardPanel`'s; this only says how they share the width.
 */
const SCOREBOARD_CLASS = 'border border-white/10 p-2 md:flex-1 md:basis-0';

/** How many rows the local board pads out to, so it stands as tall as the global one beside it. */
const LOCAL_SCOREBOARD_ROWS = 5;

interface Props {
  onNextStep: () => void;
  singSetup: SingSetup;
  highScores: HighScoreEntity[];
  song: Song;
  /** Whether leaving here plays the running order, which is what the primary can then announce. */
  queueAdvances?: boolean;
}

function HighScoresView({ onNextStep, highScores, singSetup, song, queueAdvances = true }: Props) {
  // Bottom strip, not the fixed top-right corner box: with the header on one line the local board's
  // top rows sit exactly where that box is, and it covered the score column - the same collision the
  // filter panel, the song settings and the profile page each hit.
  // Named rather than promoted: the leaderboard panel registers its own buttons above this one, so
  // whichever of those was showing used to take the fill for being first.
  const { register } = useKeyboardNav({
    title: 'High scores',
    primary: 'play-next-song-button',
    presentation: 'strip',
  });
  const editScore = useEditScore(song);
  const leaderboard = useLeaderboardPostGame({ song, singSetup });
  const profiles = useProfiles();
  const profileNames = useMemo(() => profiles.map((profile) => profile.name), [profiles]);

  // An armed score goes up as the player moves on, so the wait is never in their way — the button
  // just holds them for as long as the request takes.
  const isArmed = leaderboard.panel === 'armed';

  const goToNextStep = async () => {
    if (isArmed) await leaderboard.share();

    onNextStep();
  };

  return (
    <>
      {/* Side by side from tablet up, stacked on a phone. Full width, so the pair lines up with the
          share panel and the tip under them rather than stopping short of both. */}
      <div className="flex flex-col items-start gap-3 md:flex-row md:items-stretch md:gap-6">
        <ScoreboardPanel
          className={SCOREBOARD_CLASS}
          title="Local scoreboard"
          subtitle="This song · this device"
          data-test="highscores-container">
          {highScores.map((score, index) => {
            const isCurrentRun = score.singSetupId === singSetup.id;

            return (
              <ScoreboardRow
                key={index}
                position={index + 1}
                score={score.score}
                highlighted={isCurrentRun}
                // The score just sung and its date are the only rows that change between runs — named
                // so the visual baselines can mask them and keep the seeded rows visible
                data-test={isCurrentRun ? 'highscore-current-row' : undefined}
                // Offered only where the recorded name can actually be wrong - a slot that sang with
                // no profile on it, which scores under its "Player 2" fallback. Where a profile was
                // assigned the name is already right, so the row prints it rather than putting an
                // empty field under it.
                name={
                  isCurrentRun && !isRecordedUnderAProfile(score.name, profileNames) ? (
                    <HighScoreRename
                      index={index}
                      score={score}
                      register={register}
                      singSetupId={singSetup.id}
                      onSave={editScore}
                    />
                  ) : (
                    score.name
                  )
                }
                meta={dayjs(score.date).format('MMM D, YYYY')}
              />
            );
          })}
          {/* A device with fewer scores than this still stands as tall as the global board beside it */}
          {Array.from({ length: Math.max(0, LOCAL_SCOREBOARD_ROWS - highScores.length) }, (_, index) => (
            <ScoreboardPlaceholderRow key={`placeholder-${index}`} position={highScores.length + index + 1} />
          ))}
        </ScoreboardPanel>
        <SongLeaderboardPanel
          song={song}
          singSetup={singSetup}
          leaderboard={leaderboard}
          className={SCOREBOARD_CLASS}
        />
      </div>
      <LeaderboardSharePanel register={register} leaderboard={leaderboard} />
      <SimilarSongs song={song} register={register} />
      <div className="mt-auto">
        <SelectSongButton
          register={register}
          onClick={goToNextStep}
          label={isArmed ? 'Share score and sing a song' : 'Select song'}
          isSubmitting={leaderboard.isSubmitting}
          queueAdvances={queueAdvances}
        />
      </div>
      <LeaderboardPrompt leaderboard={leaderboard} />
    </>
  );
}

interface SelectSongButtonProps {
  register: RegisterFunc;
  onClick: () => void;
  label: string;
  isSubmitting: boolean;
  queueAdvances: boolean;
}

/**
 * Its own component so `register` runs during THIS render rather than the parent's — otherwise it
 * would be registered before the rename fields above it, putting it first in arrow navigation and in
 * the control list mirrored to the remote mic instead of last, where it appears on screen.
 */
function SelectSongButton({ register, onClick, label, isSubmitting, queueAdvances }: SelectSongButtonProps) {
  // Not while a score is in flight: the button is disabled then, and a countdown running behind a
  // control nobody can press is counting down to something they cannot stop.
  const { next, secondsLeft } = useNextUp(queueAdvances && !isSubmitting, onClick);

  const showsNextUp = queueAdvances && !isSubmitting && next !== undefined;

  return (
    <Button
      className={clsx('mt-2 w-full lg:mt-6 lg:ml-auto', showsNextUp ? 'h-[4.375rem] px-4 lg:w-[35rem]' : 'lg:w-5/12')}
      size="small"
      // Kept off the register spread: a disabled registration returns no props at all, and the e2e
      // and the remote both need this button findable while the score is in flight.
      data-test="play-next-song-button"
      leftIcon={isSubmitting ? <Icon icon="ic:baseline-refresh" className="motion-safe:animate-spin" /> : undefined}
      {...register('play-next-song-button', onClick, undefined, true, {
        disabled: isSubmitting,
        control: { type: 'button', label },
      })}>
      {showsNextUp ? <NextUpLabel next={next} secondsLeft={secondsLeft} /> : isSubmitting ? 'Sharing…' : label}
    </Button>
  );
}

export default HighScoresView;
